import TimelineItem from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineContent from '@mui/lab/TimelineContent'
import TimelineDot from '@mui/lab/TimelineDot'
import { styled } from '@mui/material/styles'
import MuiTimeline from '@mui/lab/Timeline'
import type { TimelineProps } from '@mui/lab/Timeline'
import { useTranslator } from '@/hooks'
import { useQuery } from '@tanstack/react-query'

import DialogCloseButton from '@/components/dialog/DialogCloseButton'
import { Dialog, DialogContent, Typography, CircularProgress, DialogTitle } from '@mui/material'
import { INote, Note, IUser } from '@/@data'
import { formatDateTimeDisplay } from '@/utils/formatDate'

// Styled Components
const Timeline = styled(MuiTimeline)<TimelineProps>({
  '& .MuiTimelineItem-root': {
    '&:before': {
      display: 'none'
    }
  }
})

interface INoteTimelineDialogProps {
  open: boolean
  setOpen: (open: boolean) => void
  employee?: IUser
}

export function NoteTimelineDialog({ open, employee, setOpen }: INoteTimelineDialogProps) {
  // Fetch notes data
  const { data: notesData, isLoading } = useQuery({
    ...Note.objects.findOneQueryFactory(Number(employee?.id))
  })

  if (!employee) return

  const { __ } = useTranslator()

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      scroll='body'
      fullWidth
      closeAfterTransition={false}
      sx={{
        '& .MuiDialog-paper': { overflow: 'visible', maxWidth: '600px' }
      }}
    >
      <DialogCloseButton onClick={handleClose} sx={{ position: 'absolute', right: 0, top: 0, zIndex: 1302 }}>
        <i className='tabler-x' />
      </DialogCloseButton>
      <DialogTitle className='text-left text-[18px] font-medium border-b border-[#2F2B3D66] mb-4'>
        {__('Notes Timeline')}
      </DialogTitle>

      <DialogContent>
        {isLoading ? (
          <div className='flex justify-center p-6'>
            <CircularProgress size={24} />
          </div>
        ) : (
          <Timeline>
            {(() => {
              const notes: INote[] = Array.isArray(notesData)
                ? (notesData as INote[])
                : Array.isArray((notesData as any)?.data)
                  ? ((notesData as any).data as INote[])
                  : notesData
                    ? [notesData as unknown as INote]
                    : []

              if (notes.length === 0) {
                return <Typography className='mbe-2'>{__('No notes yet')}</Typography>
              }

              return notes
                .sort((a: INote, b: INote) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                .map((note: INote) => (
                  <TimelineItem key={note.id}>
                    <TimelineSeparator>
                      <TimelineDot color='primary' />
                      <TimelineConnector sx={{ backgroundColor: '#3551f2' }} />
                    </TimelineSeparator>
                    <TimelineContent>
                      <div className='flex items-center justify-between flex-wrap gap-x-4 pbe-[7px]'>
                        <Typography className='text-textPrimary font-medium'>
                          {note?.create_user?.first_name} - {note?.create_user?.employee_id}
                        </Typography>
                        <Typography variant='caption'>{formatDateTimeDisplay(note.created_at)}</Typography>
                      </div>
                      <Typography className='mbe-2'>
                        {typeof note.data === 'object' && note.data !== null ? note.data.message : note.data || ''}
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                ))
            })()}
          </Timeline>
        )}
      </DialogContent>
    </Dialog>
  )
}
